/**
 * Athos v1 API request handler.
 * Identity travels as query params. `beacon=true` is set on every request
 * so the API returns a `responseId` for Beacon 2.0 events.
 */

import { AthosParams } from './athos-params'
import { getApiOrigin } from '../athos-utils'
import { isAbortError, isRetryableStatus } from '../athos-errors.mjs'

const REQUEST_TIMEOUT_MS = 8000

export class AthosFetcher {
  constructor(category) {
    this.params = new AthosParams(category)
  }

  generateHeaders() {
    return new Headers({
      Accept: 'application/json'
    })
  }

  generateRequestInit() {
    return {
      method: 'GET',
      headers: this.generateHeaders()
    }
  }

  generateOrigin() {
    return getApiOrigin(this.params.siteID)
  }

  updateParams() {
    this.params.set('beacon', 'true')
    this.params.setUserID()
    this.params.setSessionID()
    this.params.setPageLoadID()
    this.params.setSiteIDParam()
  }

  generateURL(endpoint = '') {
    const origin = this.generateOrigin()
    const params = this.params.toString()
    return new URL(`${origin}${endpoint}?${params}`)
  }

  async fetch(controller) {
    if (!this.params.siteID) {
      throw new Error('Athos request skipped: missing site ID')
    }

    const url = this.generateURL()
    return this.request(url, controller?.signal, true)
  }

  async request(url, externalSignal, allowRetry) {
    const attemptController = new AbortController()
    const requestInit = this.generateRequestInit()
    requestInit.signal = attemptController.signal

    const onExternalAbort = () => {
      if (!attemptController.signal.aborted) attemptController.abort('replaced')
    }
    if (externalSignal) {
      if (externalSignal.aborted) attemptController.abort()
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true })
    }

    let timedOut = false
    const timeout = setTimeout(() => {
      timedOut = true
      attemptController.abort()
    }, REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(url, requestInit)

      if (!response.ok) {
        if (allowRetry && isRetryableStatus(response.status)) {
          return this.request(url, externalSignal, false)
        }
        throw new Error(`Athos request failed: ${response.status} ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      if (isAbortError(error, externalSignal)) return undefined

      if (allowRetry) return this.request(url, externalSignal, false)

      if (timedOut) {
        throw new Error(`Athos request timed out after ${REQUEST_TIMEOUT_MS}ms`)
      }
      throw error
    } finally {
      clearTimeout(timeout)
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }
}
